import {
  type Section,
  type SubmissionFormData,
  type TableRow,
  type ValidationErrors,
} from "@/components/DirectorySubmissions/Form/types";

// Helper function to check if HTML content is effectively empty
const isEffectivelyEmpty = (value: string): boolean => {
  if (!value || typeof value !== "string") return true;

  // Create a temporary element to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = value.trim();

  // Get the text content, which strips all HTML tags
  const textContent = tempDiv.textContent || tempDiv.innerText || "";

  // Check if the text content is empty after trimming
  return textContent.trim() === "";
};

export function validateForm(
  formData: SubmissionFormData,
  sections: Section[],
): { validationErrors: ValidationErrors; invalidFieldCount: number } {
  const validationErrors: ValidationErrors = {};
  let invalidFieldCount = 0;

  sections
    .flatMap((section) => section.questions)
    .forEach((question) => {
      const isRequired = question.required ?? true;
      if (!isRequired) return;

      const value = formData[question.id];

      switch (question.type) {
        case "text":
        case "textarea": {
          // Use the new helper function instead of simple string check
          if (isEffectivelyEmpty(value as string)) {
            validationErrors[question.id] = "Field is empty";
            invalidFieldCount += 1;
          }
          break;
        }

        case "table": {
          const tableValue = value as TableRow[] | undefined;
          if (!tableValue || tableValue.length === 0) {
            validationErrors[question.id] = "Field contains no rows";
            invalidFieldCount += 1;
          } else {
            // Check if any field in the table has content
            let hasAnyContent = false;

            // Iterate through rows and cells to find any content
            for (const row of tableValue) {
              for (const cell of row.value) {
                if (typeof cell === "string" && !isEffectivelyEmpty(cell)) {
                  hasAnyContent = true;
                  break;
                } else if (typeof cell === "boolean") {
                  hasAnyContent = true;
                  break;
                } else if (Array.isArray(cell) && cell.length > 0) {
                  hasAnyContent = true;
                  break;
                } else if (
                  typeof cell === "object" &&
                  cell !== null &&
                  Object.keys(cell).length > 0
                ) {
                  hasAnyContent = true;
                  break;
                } else if (typeof cell === "number") {
                  hasAnyContent = true;
                  break;
                }
              }
              if (hasAnyContent) break;
            }

            // Only mark as invalid if there's no content at all
            if (!hasAnyContent) {
              validationErrors[question.id] = "All fields are empty";
              invalidFieldCount += 1;
            }
          }
          break;
        }

        case "highlight": {
          const highlightValue = value as Record<string, any> | undefined;
          // Check if this is a directory matter (ID starts with PM or CM)
          const isDirectoryMatter =
            question.id.startsWith("PM") || question.id.startsWith("CM");

          if (!highlightValue) {
            validationErrors[question.id] = "All fields are empty";
            invalidFieldCount += 1;
            break;
          }

          // For directory matters, validate differently - consider valid if ANY field has content
          if (isDirectoryMatter) {
            // Check if at least one field has meaningful content
            const hasAnyContent = Object.entries(highlightValue).some(
              ([key, fieldValue]) => {
                // Skip metadata fields
                if (key === "id" || key === "included_order") return false;

                // More strictly check if the field has actual content
                if (typeof fieldValue === "string") {
                  return !isEffectivelyEmpty(fieldValue);
                } else if (typeof fieldValue === "boolean") {
                  return true; // Boolean values are considered content
                } else if (Array.isArray(fieldValue)) {
                  return fieldValue.length > 0; // Non-empty arrays are considered content
                } else if (
                  typeof fieldValue === "object" &&
                  fieldValue !== null
                ) {
                  return Object.keys(fieldValue).length > 0; // Non-empty objects are considered content
                } else if (typeof fieldValue === "number") {
                  return true; // Numbers are considered content
                }
                // Null, undefined, or empty values don't count as content
                return false;
              },
            );

            // If no meaningful content, mark as invalid in the UI
            if (!hasAnyContent) {
              validationErrors[question.id] = "All fields are empty";
              invalidFieldCount += 1;
            }
            // Even if the matter is considered valid for UI purposes,
            // still count individual missing required fields toward the total
            else {
              // Count missing required fields for matter validation statistics
              let missingRequiredFields = 0;

              // Check each field - we need to track which fields are required
              // Based on createMatterQuestion in Sections.tsx:
              // Fields 0-4 and 7 are required
              // Fields 5, 6, and 8 are optional
              const requiredFieldIndexes = [0, 1, 2, 3, 4, 7];

              for (const index of requiredFieldIndexes) {
                const fieldValue = highlightValue[index.toString()];
                let hasContent = false;

                // Use the same criteria as the higher-level hasAnyContent check
                if (typeof fieldValue === "string") {
                  hasContent = !isEffectivelyEmpty(fieldValue);
                } else if (typeof fieldValue === "boolean") {
                  hasContent = true;
                } else if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                  hasContent = true;
                } else if (
                  typeof fieldValue === "object" &&
                  fieldValue !== null &&
                  Object.keys(fieldValue).length > 0
                ) {
                  hasContent = true;
                } else if (typeof fieldValue === "number") {
                  hasContent = true;
                }

                if (!hasContent) {
                  missingRequiredFields += 1;
                }
              }

              // Add to total count but don't mark this field as invalid in the UI
              invalidFieldCount += missingRequiredFields;
            }
          }
          break;
        }

        default:
          // Handle any other question types if necessary
          break;
      }
    });

  return { validationErrors, invalidFieldCount };
}
